// EmptyState - Estados vacíos atractivos con ilustraciones
import React, { type ReactNode, type ComponentType } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: ReactNode | ComponentType<{ size?: number; className?: string }>;
  title: string;
  description?: string;
  action?: ReactNode | {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({ 
  icon, 
  title, 
  description, 
  action,
  className 
}: EmptyStateProps) {
  // Handle icon rendering
  const renderIcon = () => {
    if (!icon) return null;
    
    // If it's a React component (function or ComponentType), render it
    if (typeof icon === 'function') {
      const IconComponent = icon as ComponentType<{ size?: number; width?: number; height?: number; className?: string }>;
      return (
        <div className="text-gray-400 mb-4">
          <IconComponent width={48} height={48} className="text-gray-400" />
        </div>
      );
    }
    
    // Check if it's already a valid React element
    if (React.isValidElement(icon)) {
      return (
        <div className="text-gray-400 mb-4">
          {icon}
        </div>
      );
    }
    
    // Skip rendering if it's an invalid object type (prevent React errors)
    // This handles edge cases like uninitialized components or hot-reload artifacts
    if (typeof icon === 'object' && icon !== null) {
      console.warn('[EmptyState] Invalid icon prop received - skipping render:', icon);
      return null;
    }
    
    // Fallback for primitives (string, number, etc.)
    return (
      <div className="text-gray-400 mb-4">
        {icon as ReactNode}
      </div>
    );
  };

  return (
    <Card className={cn('p-12 text-center', className)}>
      <div className="flex flex-col items-center justify-center space-y-4">
        {renderIcon()}
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-500 max-w-md">{description}</p>
        )}
        {action && (
          typeof action === 'object' && action !== null && 'onClick' in action ? (
            <Button onClick={action.onClick} className="mt-4">
              {action.label}
            </Button>
          ) : (
            <div className="mt-4">{action}</div>
          )
        )}
      </div>
    </Card>
  );
}

